import React, {useCallback, useEffect, useState} from 'react';
import {CountdownBadge} from '../../common/countdown';
import {SpokenLine, useSpeech} from '../../common/spokenLine';

// The game runs on the server (server/games/allegedly_true/director.rb): it plays the narration, the fact
// read-aloud, the reveal sting and lights through the show engine, and moves on by itself in auto. Screens only show it.

export const AllegedlyTrue = ({isControl, receiveEvents, clockDrift}) => {

    const [state, setState] = useState();

    const fetchState = useCallback(() => {
        fetch('/allegedly-true/state', {credentials: 'include'})
            .then(r => r.json())
            .then(setState)
            .catch(() => {});
    }, []);

    // The game changed (the server says {update}); subtitles and the rest of the show's messages don't concern it.
    useEffect(() => receiveEvents && receiveEvents((d) => { if (d && d.update) fetchState(); }), [fetchState, receiveEvents]);
    useEffect(() => { fetchState(); }, [fetchState]);

    if (!state) return null;

    if (isControl) return <AllegedlyTrueControl state={state} onAdvance={fetchState} clockDrift={clockDrift} receiveEvents={receiveEvents}/>;
    return <AllegedlyTruePlayer state={state} onVote={fetchState} clockDrift={clockDrift}/>;
};

// ────────────────────────────────────────────────────────────────────────────
// Control view (big screen + bottom moderator bar)
// ────────────────────────────────────────────────────────────────────────────

const AllegedlyTrueControl = ({state, onAdvance, clockDrift, receiveEvents}) => {
    const {facts, currentFactId, currentPhase, currentTally, auto} = state;
    const speech = useSpeech(receiveEvents);
    const currentFact = facts.find(f => f.id === currentFactId) || facts[0];
    const factIndex = facts.findIndex(f => f.id === currentFact?.id);
    const isNarration = currentFact?.display_as === 'narration';
    const isTransition = currentPhase === 'transition';
    const isVoting = currentPhase === 'voting';
    const isRevealed = currentPhase === 'revealed';

    const post = useCallback((path, body) => {
        fetch(path, {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        })
            .then(r => r.json())
            .then(() => onAdvance && onAdvance())
            .catch(() => {});
    }, [onAdvance]);
    const advance = (direction) => post('/allegedly-true/advance', {direction});

    const factsOnly = facts.filter(f => f.display_as !== 'narration');
    const factPositionInRun = factsOnly.findIndex(f => f.id === currentFact?.id);
    const phaseLabel = isNarration
        ? (factIndex === 0 ? 'Intro' : 'Outro')
        : `Fact ${factPositionInRun + 1} / ${factsOnly.length} — ${currentPhase}`;

    // The narration stays on screen through the fact's voting and reveal.
    const phaseKey = currentFact ? `${currentFact.id}:${currentPhase}` : null;
    const narrationText = currentFact?.transition_text || null;
    const spoken = isTransition
        ? (speech && speech.id === `allegedly:narration:${phaseKey}` ? speech : null)
        : (narrationText ? {text: narrationText, endedAt: true} : null);

    return (
        <>
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: '#000',
            zIndex: -1,
            pointerEvents: 'none',
        }}/>
        <div style={{
            width: '1500px',
            height: '900px',
            alignSelf: 'flex-start',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            color: 'white',
        }}>
            <style>{`
                @keyframes factFadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
            `}</style>

            {/* TOP — AiSpeech. minHeight so short narrations don't shift content;
                if a narration runs over, the layout below shifts down rather than clipping. */}
            <div style={{fontSize: 50, textAlign: 'center', width: '1500px', minHeight: 300, padding: '24px 60px', boxSizing: 'border-box'}}>
                <SpokenLine speech={spoken} clockDrift={clockDrift}/>
            </div>

            {/* MIDDLE — image left, content box right */}
            <div style={{display: 'flex', flexDirection: 'row', width: '100%', flex: 1, alignItems: 'flex-start'}}>
                {/* LEFT HALF — image */}
                <div style={{width: '800px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box'}}>
                    {currentFact?.image_path && (
                        <img
                            key={currentFact.id}
                            src={currentFact.image_path}
                            alt=""
                            style={{
                                maxWidth: '100%',
                                maxHeight: 680,
                                objectFit: 'contain',
                                animation: 'factFadeIn 800ms ease-out both',
                            }}
                        />
                    )}
                </div>

                {/* RIGHT HALF — fact box + count/reveal. Always rendered; opacity-toggled per phase. */}
                <div style={{
                    width: '750px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    boxSizing: 'border-box',
                    gap: 24,
                    opacity: !isNarration && (isVoting || isRevealed) ? 1 : 0,
                    transition: !isNarration && (isVoting || isRevealed) ? 'opacity 600ms ease' : 'none',
                }}>
                    {currentFact?.display_text && (
                        <div style={{
                            width: '100%',
                            border: '2px solid rgba(232,177,90,0.55)',
                            borderRadius: 8,
                            padding: '20px 22px',
                            background: 'linear-gradient(180deg, rgba(40,28,10,0.55) 0%, rgba(15,10,3,0.65) 100%)',
                            boxShadow: 'inset 0 1px 0 rgba(255,225,160,0.18), 4px 4px 0 rgba(0,0,0,0.6)',
                            fontSize: 38,
                            fontStyle: 'italic',
                            fontWeight: 700,
                            lineHeight: 1.35,
                            textAlign: 'center',
                            color: '#fff1d6',
                            textShadow: '0 0 18px rgba(232,177,90,0.35), 2px 2px 0 rgba(0,0,0,0.95)',
                            boxSizing: 'border-box',
                        }}>
                            {currentFact.display_text}
                        </div>
                    )}
                    {/* Voting count (replaced by reveal banner once phase advances) */}
                    {!isRevealed && (
                        <div style={{
                            fontSize: 32,
                            fontWeight: 800,
                            fontStyle: 'italic',
                            letterSpacing: 4,
                            textTransform: 'uppercase',
                            color: '#f1d28b',
                            opacity: 0.9,
                            textShadow: '0 0 14px rgba(232,177,90,0.5), 2px 2px 0 rgba(0,0,0,0.95)',
                        }}>
                            {currentTally.count} / {currentTally.total} voted
                        </div>
                    )}
                    {/* Reveal: TRUE/FALSE banner + actually + tally */}
                    {isRevealed && !isNarration && (
                        <div style={{textAlign: 'center', width: '100%'}}>
                            <div style={{
                                fontSize: 92,
                                fontWeight: 900,
                                fontStyle: 'italic',
                                letterSpacing: 6,
                                marginBottom: 18,
                                color: currentFact.display_as === 'true' ? '#36ff36' : '#ff5a5a',
                                textShadow: `0 0 24px ${currentFact.display_as === 'true' ? '#36ff36' : '#ff5a5a'}aa, 4px 4px 0 rgba(0,0,0,0.95)`,
                            }}>
                                {currentFact.display_as === 'true' ? 'TRUE' : 'FALSE'}
                            </div>
                            {currentFact.display_as === 'false' && currentFact.true_fact && (
                                <div style={{
                                    fontSize: 26,
                                    opacity: 0.95,
                                    marginBottom: 18,
                                    fontStyle: 'italic',
                                    color: '#fff1d6',
                                    textShadow: '0 0 12px rgba(232,177,90,0.4), 2px 2px 0 rgba(0,0,0,0.9)',
                                }}>
                                    <span style={{
                                        fontStyle: 'normal',
                                        fontWeight: 800,
                                        letterSpacing: 3,
                                        fontSize: 18,
                                        opacity: 0.7,
                                        marginRight: 10,
                                    }}>ACTUALLY</span>
                                    "{currentFact.true_fact}"
                                </div>
                            )}
                            <div style={{display: 'flex', justifyContent: 'center', gap: 48, fontSize: 36, fontWeight: 900, fontStyle: 'italic', letterSpacing: 2}}>
                                <span style={{
                                    color: '#36ff36',
                                    textShadow: '0 0 14px rgba(54,255,54,0.7), 2px 2px 0 rgba(0,0,0,0.95)',
                                }}>
                                    {currentTally.true ?? 0} TRUE
                                </span>
                                <span style={{
                                    color: '#ff5a5a',
                                    textShadow: '0 0 14px rgba(255,90,90,0.7), 2px 2px 0 rgba(0,0,0,0.95)',
                                }}>
                                    {currentTally.false ?? 0} FALSE
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </div>
        {/* Moderator control bar — sibling to the stage so it floats in the
            unprojected viewport area (same spot as Trilemma's fake-battle inputs). */}
        <div style={{
            position: 'absolute',
            width: '500px',
            bottom: 34,
            left: 800,
            right: 0,
            margin: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 16,
        }}>
            <button onClick={() => advance('back')} style={controlBtnStyle}>← Back</button>
            <div style={{fontSize: 16, color: 'white', opacity: 0.7, padding: '0 8px'}}>
                {phaseLabel}
            </div>
            <button onClick={() => advance('next')} style={{...controlBtnStyle, fontWeight: 700}}>Next →</button>
            <button
                onClick={() => post('/allegedly-true/auto', {auto: !auto})}
                style={{
                    ...controlBtnStyle,
                    color: auto ? '#36ff36' : '#ffd24a',
                    borderColor: auto ? '#36ff36' : '#ffd24a',
                }}
            >{auto ? 'AUTO ⏵' : 'MANUAL ⏸'}</button>
        </div>
        </>
    );
};

const controlBtnStyle = {
    border: '1px white solid',
    padding: '10px 20px',
    fontSize: 22,
    color: 'white',
    backgroundColor: 'black',
    cursor: 'pointer',
};

// ────────────────────────────────────────────────────────────────────────────
// Player view (phone)
// ────────────────────────────────────────────────────────────────────────────

const AllegedlyTruePlayer = ({state, onVote, clockDrift}) => {
    const {facts, currentFactId, currentPhase, votingEndsAt, yourVote: serverVote, yourScore} = state;
    const currentFact = facts.find(f => f.id === currentFactId) || facts[0];
    const factIndex = facts.findIndex(f => f.id === currentFact?.id);
    const isNarration = currentFact?.display_as === 'narration';
    const isOutro = isNarration && factIndex === facts.length - 1;
    const isVoting = currentPhase === 'voting';
    const isRevealed = currentPhase === 'revealed';
    const factsOnly = facts.filter(f => f.display_as !== 'narration');
    const factPositionInRun = factsOnly.findIndex(f => f.id === currentFact?.id);

    // Optimistic local vote — set immediately on tap, cleared on fact change
    // (so the next fact's voting state isn't pre-locked) or rolled back on
    // server failure. Falls back to the server-confirmed value otherwise.
    const [optimisticVote, setOptimisticVote] = useState(null);
    useEffect(() => { setOptimisticVote(null); }, [currentFactId]);
    const yourVote = optimisticVote ?? serverVote;

    const [submitting, setSubmitting] = useState(false);
    const submitVote = (vote) => {
        if (submitting || yourVote) return;
        setSubmitting(true);
        setOptimisticVote(vote);
        fetch('/allegedly-true/vote', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({vote}),
        })
            .then(r => {
                if (!r.ok) throw new Error('vote rejected');
                return r.json();
            })
            .then(() => onVote && onVote())
            .catch(() => setOptimisticVote(null))
            .finally(() => setSubmitting(false));
    };

    if (!currentFact) {
        return <div style={playerWrapStyle}><div style={{fontSize: 28, opacity: 0.7}}>Loading…</div></div>;
    }

    if (isOutro) {
        return <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            textAlign: 'center',
        }}>
            <div style={{fontSize: 36, opacity: 0.85, marginBottom: 16}}>Your Score:</div>
            {yourScore && (
                <div style={{fontSize: 120, fontWeight: 'bold', color: '#36ff36'}}>
                    {yourScore.correct} / {yourScore.total}
                </div>
            )}
        </div>;
    }

    if (isNarration) {
        return <img src="/titles/allegedly-true-portrait.jpg" alt="Allegedly True" style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        }}/>;
    }

    return <div style={playerFactWrapStyle}>
        {isVoting && votingEndsAt && (
            <CountdownBadge key={currentFactId} roundEnd={votingEndsAt} clockDrift={clockDrift || 0}/>
        )}
        {currentFact.image_path && (
            <img src={currentFact.image_path} alt="" style={{
                width: '100%',
                height: 'auto',
                display: 'block',
            }}/>
        )}

        <div style={{flex: 1, padding: '24px 24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', boxSizing: 'border-box'}}>
            <div style={{
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: 5,
                textTransform: 'uppercase',
                color: '#f1d28b',
                opacity: factPositionInRun >= 0 ? 0.85 : 0,
                marginBottom: 16,
                textShadow: '0 0 10px rgba(232,177,90,0.5), 1px 1px 0 rgba(0,0,0,0.9)',
                minHeight: 16,
            }}>
                {factPositionInRun >= 0 ? `Fact ${factPositionInRun + 1} / ${factsOnly.length}` : ''}
            </div>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%'}}>
            {(isVoting || isRevealed) && currentFact.display_text && (
                <div style={{
                    fontSize: 26,
                    fontStyle: 'italic',
                    fontWeight: 700,
                    textAlign: 'center',
                    lineHeight: 1.3,
                    marginBottom: 28,
                    color: '#fff1d6',
                    textShadow: '0 0 16px rgba(232,177,90,0.4), 2px 2px 0 rgba(0,0,0,0.9)',
                }}>
                    {currentFact.display_text}
                </div>
            )}

            {isVoting && (
                <div style={{display: 'flex', gap: 16, width: '100%', maxWidth: 320}}>
                    <button
                        disabled={submitting || !!yourVote}
                        onClick={() => submitVote('true')}
                        style={{
                            ...voteBtnStyle('#36ff36', yourVote === 'true'),
                            visibility: yourVote && yourVote !== 'true' ? 'hidden' : 'visible',
                        }}
                    >TRUE</button>
                    <button
                        disabled={submitting || !!yourVote}
                        onClick={() => submitVote('false')}
                        style={{
                            ...voteBtnStyle('#ff5a5a', yourVote === 'false'),
                            visibility: yourVote && yourVote !== 'false' ? 'hidden' : 'visible',
                        }}
                    >FALSE</button>
                </div>
            )}

            {isRevealed && (
                <div style={{textAlign: 'center'}}>
                    <div style={{fontSize: 32, fontWeight: 'bold', marginBottom: 16}}>
                        {currentFact.display_as === 'true' ? 'TRUE' : 'FALSE'}
                    </div>
                    {yourVote && (
                        <div style={{
                            fontSize: 22,
                            marginBottom: 14,
                            color: yourVote === currentFact.display_as ? '#36ff36' : '#ff5a5a',
                        }}>
                            {yourVote === currentFact.display_as ? '✓ Correct!' : '✗ Wrong!'}
                        </div>
                    )}
                    {currentFact.display_as === 'false' && currentFact.true_fact && (
                        <div style={{fontSize: 16, opacity: 0.8, fontStyle: 'italic', marginTop: 8}}>
                            Actually: "{currentFact.true_fact}"
                        </div>
                    )}
                </div>
            )}
            </div>
        </div>
    </div>;
};

const playerWrapStyle = {
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 40,
};

const playerFactWrapStyle = {
    width: '100%',
    height: '100dvh',
    boxSizing: 'border-box',
    paddingTop: 'calc(56px + env(safe-area-inset-top))',
    paddingBottom: 'calc(env(safe-area-inset-bottom))',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};

const voteBtnStyle = (color, isSelected = false) => ({
    flex: 1,
    padding: '28px 0',
    border: 'none',
    borderRadius: 12,
    background: isSelected
        ? `linear-gradient(180deg, ${color} 0%, ${color}cc 100%)`
        : 'linear-gradient(180deg, rgba(20,20,20,0.85) 0%, rgba(8,8,8,0.85) 100%)',
    color: isSelected ? '#0a0a0a' : color,
    fontSize: 38,
    fontWeight: 900,
    fontStyle: 'italic',
    fontFamily: 'inherit',
    letterSpacing: 4,
    textTransform: 'uppercase',
    textShadow: isSelected
        ? '1px 1px 0 rgba(255,255,255,0.4)'
        : `0 0 14px ${color}cc, 2px 2px 0 rgba(0,0,0,0.95)`,
    boxShadow: isSelected
        ? `inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -3px 0 rgba(0,0,0,0.35), 0 0 28px ${color}aa, 4px 4px 0 rgba(0,0,0,0.55)`
        : `inset 0 0 0 3px ${color}, inset 0 1px 0 rgba(255,255,255,0.08), 0 0 18px ${color}55, 4px 4px 0 rgba(0,0,0,0.55)`,
    cursor: 'pointer',
    transition: 'transform 100ms ease',
});
